import { useTranslation } from 'react-i18next';

import {
	AwardRibbonOutline,
	Box,
	Flex,
	HomeOutline,
	PlantOutline,
	ShoppingCartOutline,
	Show,
	useBreakpointValue,
	UserGroupOutline,
} from '@bonusly/design-system';
import { Permission, ProtectedContent, ProtectedContentWithPermissionOrFeatureFlag, useAnalytics, useIdentity } from '@bonusly/who-am-i';

import { useRewardAnalytics } from '../../features/rewards/hooks/useRewardAnalytics';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { useUserProfile } from '../../hooks/useUserProfile';
// TODO - Refactor to use helpers from from @bonusly/utilities
import { Variant } from '../FeatureFlag';
import { AnalyticsMenu } from '../Menu/AnalyticsMenu';
import { SearchModal } from '../SearchModal';
import { CompanyLogo } from './CompanyLogo';
import { useNavbarRewards } from './hooks/useNavbarRewards';
import { InviteUsers } from './invite-users';
import { MonarchLink } from './MonarchLink';
import { NavbarShell } from './NavbarShell';
import { NavCircle } from './NavCircle';
import { NavLink } from './NavLink';
import { NavMenu } from './NavMenu';
import { NavNotificationMenu } from './NavNotificationMenu';

export const Navbar = () => {
	// TODO: Remove useUserProfile and fetch user from '@bonusly/who-am-i' package
	const { user } = useUserProfile();
	const { isUserIdentified } = useIdentity();
	const { company } = useCompanyProfile();
	const { t } = useTranslation('navigation');
	const rewardAnalytics = useRewardAnalytics();
	const { track } = useAnalytics();
	const isMobile = useBreakpointValue({ base: true, sm: false });

	const isManager = user?.is_manager;

	const displayFor = useBreakpointValue({
		base: 'mobile',
		xs: 'desktop',
	});

	const { loadingCompanyCategories, showRewardsNavigation } = useNavbarRewards();

	const canAccessAwards = user?.admin || (user?.show_awards && company?.has_claimable_awards);

	const trackRewardNavigation = () => {
		rewardAnalytics.trackRewardNavigation({ pathway: 'menu' });
	};

	const isLoaded = !!user && isUserIdentified && !!company && !loadingCompanyCategories;

	return (
		<NavbarShell logo={<CompanyLogo />}>
			{isLoaded && (
				<Flex
					alignItems="center"
					as="ul"
					columnGap={1}
					display={{ base: 'none', xs: 'flex' }}
					flexBasis={0}
					flexGrow={1}
					flexDir="row"
					id="primary-nav-items"
					justifyContent="center"
					listStyleType="none"
				>
					<NavLink icon={<HomeOutline />} to="/">
						{t('links.home')}
					</NavLink>

					<ProtectedContentWithPermissionOrFeatureFlag
						flag={[Variant.AchieveEarlyAccess, Variant.AchieveLimitedRelease]}
						permissions={Permission.CanAccessAchieve}
					>
						<MonarchLink icon={<PlantOutline />} to="/growth">
							{t('links.growth')}
						</MonarchLink>

						{isManager && (
							<MonarchLink icon={<UserGroupOutline />} to="/team">
								{t('links.team')}
							</MonarchLink>
						)}
					</ProtectedContentWithPermissionOrFeatureFlag>

					{showRewardsNavigation && (
						<Box onClick={trackRewardNavigation}>
							<MonarchLink icon={<ShoppingCartOutline />} to="/rewards" id="navbar-rewards">
								{t('links.rewards')}
							</MonarchLink>
						</Box>
					)}

					{canAccessAwards && (
						<Box onClick={() => track('Incentives Navigation', { pathway: 'menu' })}>
							<MonarchLink icon={<AwardRibbonOutline />} to="/incentives" id="navbar-awards">
								{t('links.incentives')}
							</MonarchLink>
						</Box>
					)}

					{user.show_analytics && (
						<ProtectedContent permissions={Permission.CanAccessAnalytics}>
							<AnalyticsMenu />
						</ProtectedContent>
					)}
				</Flex>
			)}

			<Flex flexBasis={0} flexGrow={1} justifyContent="flex-end" listStyleType="none">
				<Flex id="primary-nav-menu" columnGap={3} alignItems="center">
					{isLoaded ? (
						<>
							<InviteUsers />
							{!isMobile && <SearchModal />}
							<Show above="xs">
								<NavNotificationMenu user={user} />
							</Show>
							<NavMenu displayFor={displayFor} />
						</>
					) : (
						<>
							<NavCircle />
							<Show above="xs">
								<NavCircle />
							</Show>
							<NavCircle />
						</>
					)}
				</Flex>
			</Flex>
		</NavbarShell>
	);
};
